<template>
  <div class="preview-wrap">
    <div v-if="scheduled && scheduledText" class="schedule-banner">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="12" cy="12" r="9" />
        <polyline points="12 7 12 12 15 14" />
      </svg>
      <span>{{ scheduledText }}</span>
    </div>

    <div class="fb-post">
      <div class="post-top">
        <thumbnail :src="pagePicture" :username="pageName" size="40px" />
        <div>
          <div class="post-user">{{ pageName }}</div>
          <div class="post-sub">
            <span>{{
              scheduled
                ? $t('HUB.FB_PAGE.SCHEDULED')
                : $t('HUB.FB_PAGE.JUST_NOW')
            }}</span>
          </div>
        </div>
        <span class="post-dots" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="1.6" />
            <circle cx="12" cy="12" r="1.6" />
            <circle cx="19" cy="12" r="1.6" />
          </svg>
        </span>
      </div>

      <!-- previewHtml is HTML-escaped before tags are wrapped -->
      <div v-if="message.trim()" class="post-text" v-html="previewHtml" />

      <div v-if="images.length" class="post-media">
        <div
          v-for="(img, i) in images"
          :key="`pv-${img.id}`"
          class="p-slide"
          :class="{ active: i === pvIndex }"
        >
          <video v-if="img.type === 'video'" :src="img.url" controls />
          <img v-else :src="img.url" :alt="`Post image ${i + 1}`" />
        </div>
        <button
          v-show="images.length > 1 && pvIndex > 0"
          class="car-arrow prev"
          type="button"
          :aria-label="$t('HUB.FB_PAGE.BACK')"
          @click="pvGo(-1)"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          v-show="images.length > 1 && pvIndex < images.length - 1"
          class="car-arrow next"
          type="button"
          :aria-label="$t('HUB.FB_PAGE.NEXT')"
          @click="pvGo(1)"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
        <div v-if="images.length > 1" class="p-count">
          {{ pvIndex + 1 }}/{{ images.length }}
        </div>
        <div v-if="images.length > 1" class="post-dots-row">
          <span
            v-for="(img, i) in images"
            :key="`pv-dot-${img.id}`"
            :class="{ active: i === pvIndex }"
          />
        </div>
      </div>

      <div class="post-actions" aria-hidden="true">
        <button type="button">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M7 22V11" />
            <path
              d="M7 11l3.5-7a1.6 1.6 0 0 1 3 .8V9h5.2a2 2 0 0 1 1.96 2.36l-1.27 7A2 2 0 0 1 16.45 20H7"
            />
          </svg>
          {{ $t('HUB.FB_PAGE.PREVIEW_LIKE') }}
        </button>
        <button type="button">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path
              d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.1A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z"
            />
          </svg>
          {{ $t('HUB.FB_PAGE.PREVIEW_COMMENT') }}
        </button>
        <button type="button">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
          </svg>
          {{ $t('HUB.FB_PAGE.PREVIEW_SHARE') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';

export default {
  components: { Thumbnail },
  props: {
    pageName: {
      type: String,
      default: '',
    },
    pagePicture: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
    images: {
      type: Array,
      default: () => [],
    },
    scheduled: {
      type: Boolean,
      default: false,
    },
    scheduledText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      pvIndex: 0,
    };
  },
  computed: {
    // Escape the message first, then style #hashtags / @mentions. Safe to
    // render with v-html because user input is escaped before wrapping.
    previewHtml() {
      const escaped = this.message
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return escaped.replace(/([#@][\w.]+)/g, '<span class="tag">$1</span>');
    },
  },
  watch: {
    images() {
      this.pvIndex = 0;
    },
  },
  methods: {
    pvGo(delta) {
      this.pvIndex = Math.max(
        0,
        Math.min(this.images.length - 1, this.pvIndex + delta)
      );
    },
  },
};
</script>

<style scoped>
.preview-wrap {
  --cw-green-tint: #eaf6f0;
  --cw-green-border: #b7ddca;
  --cw-green-dark: #1f845a;
  --fb-surface: #f0f2f5;
  --fb-blue: #1877f2;
  --ink: #262626;
  --muted: #6b7280;
  --line: #d1d5db;
  --line-soft: #efefef;

  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  overflow-y: auto;
  background: var(--fb-surface);
}
.preview-wrap *,
.preview-wrap *::before,
.preview-wrap *::after {
  box-sizing: border-box;
}
.schedule-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 460px;
  background: var(--cw-green-tint);
  border: 1px solid var(--cw-green-border);
  color: var(--cw-green-dark);
  font-size: 13px;
  font-weight: 600;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 14px;
}
.schedule-banner svg {
  width: 16px;
  height: 16px;
  flex: none;
}
.fb-post {
  width: 100%;
  max-width: 460px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
.post-top {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px 8px;
}
.post-user {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
  line-height: 1.2;
}
.post-sub {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--muted);
  line-height: 1.2;
  margin-top: 2px;
}
.post-dots {
  margin-left: auto;
  color: var(--muted);
}
.post-text {
  padding: 4px 14px 10px;
  font-size: 15px;
  color: var(--ink);
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}
.post-text .tag {
  color: var(--fb-blue);
  font-weight: 500;
}
.post-media {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #000;
}
.post-media .p-slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.25s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.post-media .p-slide.active {
  opacity: 1;
}
.post-media img,
.post-media video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.car-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.85);
  border: none;
  cursor: pointer;
  display: grid;
  place-items: center;
  color: #262626;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  z-index: 2;
}
.car-arrow.prev {
  left: 12px;
}
.car-arrow.next {
  right: 12px;
}
.car-arrow svg {
  width: 16px;
  height: 16px;
}
.post-media .p-count {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 20px;
}
.post-dots-row {
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  display: flex;
  gap: 5px;
  justify-content: center;
}
.post-dots-row span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
}
.post-dots-row span.active {
  background: #fff;
}
.post-actions {
  display: flex;
  align-items: center;
  border-top: 1px solid var(--line-soft);
  padding: 4px 6px;
}
.post-actions button {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  background: transparent;
  border: none;
  color: #65676b;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 8px;
  border-radius: 6px;
  cursor: default;
}
.post-actions svg {
  width: 18px;
  height: 18px;
}
</style>
